import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Button,
    Grid,
    Typography,
    IconButton,
    InputAdornment
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL, API_INVENTORY } from '../../../helpers/urlHelper';

const AddProductPopup = ({ open, handleClose }) => {
    // Estados para campos
    const [productSuffix, setProductSuffix] = useState('');
    const [code, setCode] = useState('');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [price, setPrice] = useState('');
    const [stock, setStock] = useState('');
    const [uom, setUom] = useState('');
    const [createdAt, setCreatedAt] = useState('');

    // Especificaciones del producto
    const [dosage, setDosage] = useState('');
    const [presentation, setPresentation] = useState('');
    const [laboratory, setLaboratory] = useState('');
    const [activeIngredient, setActiveIngredient] = useState('');
    const [expiryDate, setExpiryDate] = useState('');

    // Al abrir, prefill createdAt con fecha de hoy; al cerrar, limpiar todo
    useEffect(() => {
        if (open) {
            const today = new Date().toISOString().split('T')[0];
            setCreatedAt(today);
        } else {
            setProductSuffix('');
            setCode('');
            setName('');
            setDescription('');
            setPrice('');
            setStock('');
            setUom('');
            setCreatedAt('');
            setDosage('');
            setPresentation('');
            setLaboratory('');
            setActiveIngredient('');
            setExpiryDate('');
        }
    }, [open]);

    const handleSave = () => {
        // Validar obligatorios
        if (!productSuffix || !code || !name || !price || !stock || !createdAt) {
            console.error('Faltan campos requeridos');
            return;
        }
        const priceValue = parseFloat(price);
        const stockValue = parseInt(stock, 10);
        if (isNaN(priceValue) || isNaN(stockValue)) {
            console.error('Price o Stock inválido');
            return;
        }
        // Formatear fechas a ISO
        const createdAtValue = new Date(createdAt).toISOString();
        const expiryDateValue = expiryDate ? new Date(expiryDate).toISOString() : null;
        // Generar ID
        const productId = `MED${productSuffix}`;
        // Objeto producto
        const newProduct = {
            id: productId,
            code,
            name,
            description,
            price: priceValue,
            stock: stockValue,
            uom,
            createdAt: createdAtValue,
            specifications: {
                dosage,
                presentation,
                laboratory,
                activeIngredient,
                expiryDate: expiryDateValue,
                updatedAt: new Date().toISOString(),
            }
        };
        console.log('Saving:', newProduct);
        fetch(`${API_BASE_URL}${API_INVENTORY}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(newProduct),
        })
            .then(res => {
                if (!res.ok) return res.text().then(t => { throw new Error(t); });
                return res.json();
            })
            .then(data => { console.log('Created:', data); handleClose(); })
            .catch(err => { console.error('Error:', err); });
    };

    return (
        <Dialog open={open} onClose={handleClose} fullScreen PaperProps={{ style: { marginLeft: 'auto', width: '35%', height: '100%' } }}>
            <DialogTitle>
                <Typography variant='h6'>Add Product</Typography>
                <IconButton onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* Columnas principales */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label='Product ID' size='small' variant='outlined'
                                   InputProps={{ startAdornment: <InputAdornment position='start'>MED</InputAdornment> }}
                                   helperText='Enter numeric part only' value={productSuffix} onChange={e => setProductSuffix(e.target.value)}
                        />
                        <TextField fullWidth label='Name' size='small' variant='outlined' sx={{ mt: 2 }}
                                   value={name} onChange={e => setName(e.target.value)}
                        />
                        <TextField fullWidth label='Stock' size='small' variant='outlined' type='number' sx={{ mt: 2 }}
                                   value={stock} onChange={e => setStock(e.target.value)}
                        />
                        <TextField fullWidth label='Code' size='small' variant='outlined' sx={{ mt: 2 }}
                                   value={code} onChange={e => setCode(e.target.value)}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label='Price' size='small' variant='outlined' type='number'
                                   value={price} onChange={e => setPrice(e.target.value)}
                        />
                        <TextField fullWidth label='UoM' size='small' variant='outlined' sx={{ mt: 2 }}
                                   value={uom} onChange={e => setUom(e.target.value)}
                        />
                        <TextField fullWidth label='Dosage' size='small' variant='outlined' sx={{ mt: 2 }}
                                   value={dosage} onChange={e => setDosage(e.target.value)}
                        />
                        <TextField fullWidth label='Presentation' size='small' variant='outlined' sx={{ mt: 2 }}
                                   value={presentation} onChange={e => setPresentation(e.target.value)}
                        />
                    </Grid>

                    {/* Descripción full width */}
                    <Grid item xs={12}>
                        <TextField fullWidth label='Description' size='small' variant='outlined' multiline rows={3}
                                   value={description} onChange={e => setDescription(e.target.value)}
                        />
                    </Grid>

                    {/* Laboratorio/Ingrediente */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label='Laboratory' size='small' variant='outlined'
                                   value={laboratory} onChange={e => setLaboratory(e.target.value)}
                        />
                        <TextField fullWidth label='Active Ingredient' size='small' variant='outlined' sx={{ mt: 2 }}
                                   value={activeIngredient} onChange={e => setActiveIngredient(e.target.value)}
                        />
                    </Grid>
                    {/* Fechas al final */}
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label='Created At' size='small' variant='outlined' type='date'
                                   InputLabelProps={{ shrink: true }} sx={{ mt: 2 }}
                                   value={createdAt} onChange={e => setCreatedAt(e.target.value)}
                        />
                        <TextField fullWidth label='Expiry Date' size='small' variant='outlined' type='date' sx={{ mt: 2 }}
                                   InputLabelProps={{ shrink: true }}
                                   value={expiryDate} onChange={e => setExpiryDate(e.target.value)}
                        />
                    </Grid>
                </Grid>
                <Grid container justifyContent='space-between' sx={{ mt: 4 }}>
                    <Button variant='outlined' onClick={handleClose}>Close</Button>
                    <Button variant='contained' color='primary' onClick={handleSave}>Save</Button>
                </Grid>
            </DialogContent>
        </Dialog>
    );
};

export default AddProductPopup;
